import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AlertController, ModalController, ToastController } from '@ionic/angular';
import { Subject } from 'rxjs';
import { finalize, switchMap, takeUntil } from 'rxjs/operators';
import { UploadDocument } from 'src/app/core/model/application.model';
import { TransferDocument } from 'src/app/core/model/document.model';
import { ApplicationService } from 'src/app/services/application/application.service';
import { CommonService } from 'src/app/services/common/common.service';
import { TransferService } from 'src/app/services/transfer/transfer.service';

@Component({
  selector: 'app-re-upload-document-modal',
  templateUrl: './re-upload-document-modal.component.html',
  styleUrls: ['./re-upload-document-modal.component.scss'],
})
export class ReUploadDocumentModalComponent implements OnInit, OnDestroy {

  //#region Inputs
  @Input() investmentId: number = 0;
  @Input() transferApplication: string = '';
  @Input() transferId: number = 0;
  @Input() applicationUuid: string = '';
  showLoader = false;
  //#endregion

  //#region Private Fields
  private destroy$: Subject<void> = new Subject<void>();
  //#endregion

  //#region Fields

  documentType: string = '';    //Type of the document user intends to upload
  documents: Array<TransferDocument> = [];   //Will contain the documents which has been already uploaded by the user
  fileformatError: boolean = false; //store if file's format is correct
  maxSizError: boolean = false; //flag to check if the size of file is more than required
  sameDocumentTypeError: boolean = false;
  sameDocumentError: boolean = false;
  //#endregion

  constructor(
    private modalController: ModalController,
    private readonly applicationService: ApplicationService,
    private readonly alertController: AlertController,
    private readonly toastController: ToastController,
    private readonly tranferService: TransferService,
    private readonly commonService: CommonService
  ) { }

  //#region Life Cycle Hooks
  ngOnInit() {
    // console.log('TransferId:', this.transferId);
    // console.log('applicationUuid:', this.applicationUuid);
    // console.log('transferApplication:', this.transferApplication);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  //#endregion

  //#region Methods

  /**
   * Executed when this modal is closed.
   */
  dismissModal() {
    this.modalController.dismiss();
  }

  /**
 * Handles uploading of a selected document file.
 * 
 * This method performs the following:
 * - Validates the selected file (type and size)
 * - Checks if a document type is selected
 * - Constructs the request body for upload
 * - Sends the file and metadata to the API
 * - Updates the local `documents` array with the uploaded file
 * - Clears the file input field after successful upload
 * 
 * @param fileInput - The HTML input element of type file (`<input type="file">`)
 * 
 * @returns void
 * 
 * @example
 * <input type="file" #fileInput (change)="uploadDocument(fileInput)" />
 * 
 * @notes
 * - Accepted file formats: jpg, jpeg, png, pdf
 * - Maximum file size: 3 MB
 * - Requires `this.documentType` to be selected before uploading
 * - Uses `takeUntil(this.destroy$)` for unsubscribing on component destroy
 */
  uploadDocument(fileInput: HTMLInputElement) {
    this.fileformatError = false;
    this.maxSizError = false;
    this.sameDocumentError = false;
    this.sameDocumentTypeError = false;

    const selectedFile = fileInput.files[0];
    if (!selectedFile) {
      alert('Please select a file to upload');
      return;
    }

    // Get the file format type
    let idxDot = selectedFile ? selectedFile.name.lastIndexOf('.') + 1 : null;
    let fileFormat = selectedFile ? selectedFile.name.substr(idxDot, selectedFile.name.length).toLowerCase() : null;

    if (!this.documentType) {
      alert('Please select a document type');
      return;
    }

    if (['jpg', 'jpeg', 'png', 'pdf'].includes(fileFormat.toLowerCase()) === false) {
      this.fileformatError = true;
      return;
    }
    this.fileformatError = false;

    //Check size limit
    if (selectedFile.size > 20000000) {
      this.maxSizError = true;
      return;
    }
    this.maxSizError = false;

    if (this.documents.some(x => x.documentType === this.documentType)) {
      this.sameDocumentTypeError = true;
      return;
    }
    this.sameDocumentTypeError = false;

    if (this.documents.some(x => x.fileName === selectedFile.name)) {
      this.sameDocumentError = true;
      return;
    }
    this.sameDocumentError = false;

    //send upload request to the api
    const body = {} as UploadDocument;
    const val = this.documentType;
    if (val) {

      this.showLoader = true;
      this.commonService.encrypt(this.applicationUuid).pipe(
        switchMap((res: any) => {
          const investmentId = res.encryptedText.replaceAll('/', 'slh').replaceAll('==', '--').replaceAll('=', '-');
          body.applicationUuid = investmentId.toString();
          body.transferApplication = this.transferApplication ?? '';
          body.transferId = this.transferId.toString() ?? "0";
          body.documentType = this.documentType;
          return this.applicationService.uploadDocument(selectedFile, body);
        }),
        takeUntil(this.destroy$),
        finalize(() => this.showLoader = false)
      ).subscribe({
        next: async (res) => {

          //Maintaining a list on the frontend
          this.documents.push({
            documentId: 0,
            documentName: selectedFile.name,
            documentType: this.documentType,
            fileName: selectedFile.name,
            transferId: this.transferId,
            originalDocument: selectedFile
          } as TransferDocument)

          const toast = await this.toastController.create({
            message: `${selectedFile.name} uploaded successfully!`,
            duration: 2000,
          });
          toast.present();

          fileInput.value = ''  //Clearing the document inside the file input
          this.documentType = '';

        },
        error: (err) => { },
      });
    }

  }

  /**
   * Deleting reuploaded document
   * @param fileIndex Index of the file to be deleted inside uploadedDocument array 
   * @param fileName 
   * @param documentid 
   */
  async deleteDocumentTransfer(fileIndex: number, fileName: string, documentid: number) {
    const alert = await this.alertController.create({
      header: 'Confirm!',
      message: `Are you sure you want to delete this document ?`,
      buttons: [
        {
          text: 'No',
          handler: () => { },
        },
        {
          text: 'Yes',
          handler: async () => {

            const toast = await this.toastController.create({
              message: `Document ${fileName} deleted successfully.`,
              duration: 2000,
            });
            toast.present();
            this.documents.splice(fileIndex, 1);

            // this.applicationService
            //   .deleteDocument(documentid, fileName)
            //   .subscribe({
            //     next: async (res) => {
            //       const toast = await this.toastController.create({
            //         message: `Document ${fileName} deleted successfully.`,
            //         duration: 2000,
            //       });
            //       toast.present();
            //       this.documents.splice(fileIndex, 1);
            //     },
            //   });
          },
        },
      ],
    });

    await alert.present();
  }

  /**
   * This method filters out the file on the document list, and provide with the particular file.
   * @param fileIndex 
   */
  viewFile(fileIndex) {
    const file = this.documents[fileIndex].originalDocument;
    if (file instanceof File) {
      const fileUrl = URL.createObjectURL(file);
      window.open(fileUrl, '_blank');
    }
  }

  /**
   * This is executed when the submit button is clicked on the modal
   * @param result Either approve or reject
   */
  submit() {
    this.tranferService.completeReupload(this.transferId).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.dismissModal(),
      error: (err: any) => {
        console.error("Something went wrong:", err);
      }
    });
  }
  //#endregion

}
